import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Button,
  Card,
  DarkSelect,
  Input,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
} from "@/components/ui";
import { confirmPopup } from "@/components/popup";

interface ServerRow {
  id: number;
  name: string;
  host: string;
  server_type: string;
  is_active: boolean;
}

interface ServersList {
  servers: ServerRow[];
}

interface BulkUserRow {
  user_id: number;
  access_id: number | null;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  server_id: number | null;
  server_name: string | null;
  endpoint_id?: number | null;
  expires_at: string;
  access_type: string;
}

interface BulkServerUsersResponse {
  items: BulkUserRow[];
}

interface UserSearchItem {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
}

interface UserSearchResponse {
  users: UserSearchItem[];
}

interface BulkRotateError {
  access_id: number;
  error: string;
}

interface BulkRotateResponse {
  ok: number;
  errors: BulkRotateError[];
}

// bulk-grant types
interface BulkGrantCandidate {
  user_id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
}

interface BulkGrantPreviewResponse {
  candidates: BulkGrantCandidate[];
  total: number;
}

interface BulkGrantError {
  user_id: number;
  error: string;
}

interface BulkGrantResponse {
  ok: number;
  errors: BulkGrantError[];
}

type Tab = "server" | "manual" | "new";
type Source = "server" | "manual";

interface ListItem extends BulkUserRow {
  extra_days: number;
}

const GRANT_SEGMENTS = [
  { value: "no_test", label: "Не брали тест", desc: "Ни разу не активировали тестовый доступ" },
  {
    value: "test_expired_no_paid",
    label: "Тест истёк, не купили",
    desc: "Взяли тест, тест закончился, paid ни разу не покупали",
  },
  {
    value: "paid_expired",
    label: "Платка истекла",
    desc: "Когда-то покупали paid, сейчас нет активной подписки",
  },
  { value: "all", label: "Все незаблокированные", desc: "Осторожно — все юзеры" },
];

const dt = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const remainingDays = (expires_at: string) =>
  Math.max(
    0,
    Math.ceil(
      (new Date(expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
    ),
  );

export default function BulkGrant() {
  const [tab, setTab] = useState<Tab>("server");

  return (
    <div className="p-4 md:p-8">
      <PageHeader title="Групповая выдача" />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <TabButton active={tab === "server"} onClick={() => setTab("server")}>
          Из сервера
        </TabButton>
        <TabButton active={tab === "manual"} onClick={() => setTab("manual")}>
          Произвольно
        </TabButton>
        <TabButton active={tab === "new"} onClick={() => setTab("new")}>
          Новая выдача
        </TabButton>
      </div>

      {tab === "new" ? (
        <NewGrantTab />
      ) : (
        <RotateTab source={tab as Source} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Таб «На подписки» — переезд старых Access → новая модель (Subscription)
// на endpoint-хост. Обёртка над /admin/grants/migrate-to-subs.
// ──────────────────────────────────────────────────────────

interface EndpointLite {
  id: number;
  name: string;
  kind: string;
  address: string;
}


// ──────────────────────────────────────────────────────────
// Таб «Из сервера» / «Произвольно» (старая логика, без изменений)
// ──────────────────────────────────────────────────────────

function RotateTab({ source }: { source: Source }) {
  // Источник списка: "srv:<id>" (старый сервер) ИЛИ "ep:<id>" (хост новой модели).
  const [sourceSel, setSourceSel] = useState<string>("");
  // target: "srv:<id>" (старый сервер, перевыпуск Access) или "ep:<id>" (хост
  // новой модели, выдача Subscription). Одна логика для обоих назначений.
  const [target, setTarget] = useState<string>("");
  const [items, setItems] = useState<ListItem[]>([]);
  const [globalDays, setGlobalDays] = useState<string>("0");
  const [submitResult, setSubmitResult] = useState<BulkRotateResponse | null>(null);
  const [hostResult, setHostResult] = useState<{ queued: number; target: string } | null>(null);
  const [typeFilter, setTypeFilter] = useState<"all" | "test" | "paid">("all");

  const rowKey = (i: { access_id: number | null; user_id: number }) =>
    i.access_id != null ? `a${i.access_id}` : `u${i.user_id}`;

  const visibleItems = useMemo(
    () =>
      typeFilter === "all"
        ? items
        : items.filter((i) => i.access_type === typeFilter),
    [items, typeFilter],
  );
  const testCount = items.filter((i) => i.access_type === "test").length;
  const paidCount = items.filter((i) => i.access_type === "paid").length;

  const servers = useQuery<ServersList>({
    queryKey: ["servers"],
    queryFn: () => api<ServersList>("/api/admin/servers"),
  });
  const endpoints = useQuery<{ endpoints: EndpointLite[] }>({
    queryKey: ["endpoints"],
    queryFn: () => api<{ endpoints: EndpointLite[] }>("/api/admin/endpoints"),
  });
  const eps = endpoints.data?.endpoints ?? [];

  const eligibleServers = useMemo(
    () => (servers.data?.servers ?? []).filter((s) => s.server_type !== "ad"),
    [servers.data],
  );

  // Единый список назначений: старые серверы + хосты новой модели.
  const destOptions = [
    ...eligibleServers
      .filter((s) => s.is_active)
      .map((s) => ({ value: `srv:${s.id}`, label: `сервер · ${s.name} (${s.server_type})` })),
    ...eps.map((e) => ({ value: `ep:${e.id}`, label: `хост · ${e.name} · ${e.address}` })),
  ];
  const isHostTarget = target.startsWith("ep:");
  const targetLabel = destOptions.find((o) => o.value === target)?.label ?? "—";

  // Единый список ИСТОЧНИКОВ: старые серверы + хосты новой модели.
  const sourceOptions = [
    ...eligibleServers.map((s) => ({
      value: `srv:${s.id}`,
      label: `сервер · ${s.name} (${s.server_type}, ${s.host})`,
    })),
    ...eps.map((e) => ({ value: `ep:${e.id}`, label: `хост · ${e.name} · ${e.address}` })),
  ];

  const sourceUsers = useQuery<BulkServerUsersResponse>({
    queryKey: ["bulk-source", sourceSel],
    queryFn: () =>
      api<BulkServerUsersResponse>(
        sourceSel.startsWith("ep:")
          ? `/api/admin/endpoints/${sourceSel.slice(3)}/users-with-subs`
          : `/api/admin/servers/${sourceSel.slice(4)}/users-with-paid`,
      ),
    enabled: source === "server" && sourceSel !== "",
  });

  useEffect(() => {
    if (source === "server" && sourceUsers.data) {
      setItems(sourceUsers.data.items.map((u) => ({ ...u, extra_days: 0 })));
      setSubmitResult(null);
      setHostResult(null);
    }
  }, [source, sourceUsers.data]);

  useEffect(() => {
    if (source === "manual") {
      setItems([]);
      setSourceSel("");
      setSubmitResult(null);
      setHostResult(null);
    }
  }, [source]);

  // На сервер можно перенести только строки со старым Access; на хост — любых юзеров.
  const rotatable = visibleItems.filter((i) => i.access_id != null);

  const submit = useMutation<BulkRotateResponse | { queued: number; target: string }>({
    mutationFn: async () => {
      if (isHostTarget) {
        const epId = Number(target.slice(3));
        // Готовый срок по каждой строке = остаток дней строки + её «+ дней»
        // (ровно как в колонке «будет до»). Дедуп по user_id — максимальный срок.
        const byUser = new Map<number, number>();
        for (const i of visibleItems) {
          const days = remainingDays(i.expires_at) + Math.max(0, Math.floor(i.extra_days || 0));
          byUser.set(i.user_id, Math.max(byUser.get(i.user_id) ?? 0, days));
        }
        const user_days = Array.from(byUser.entries()).map(([user_id, days]) => ({ user_id, days }));
        return api<{ queued: number; target: string }>("/api/admin/grants/migrate-to-subs", {
          method: "POST",
          body: JSON.stringify({
            target_endpoint_id: epId,
            user_days,
            access_type: "paid",
          }),
        });
      }
      const srvId = Number(target.slice(4));
      return api<BulkRotateResponse>("/api/admin/grants/bulk-rotate", {
        method: "POST",
        body: JSON.stringify({
          target_server_id: srvId,
          items: rotatable.map((i) => ({
            access_id: i.access_id,
            extra_days: Math.max(0, Math.floor(i.extra_days || 0)),
          })),
        }),
      });
    },
    onSuccess: (r) => {
      if (isHostTarget) setHostResult(r as { queued: number; target: string });
      else {
        setSubmitResult(r as BulkRotateResponse);
        if (source === "server") sourceUsers.refetch();
      }
    },
  });

  const applyGlobal = () => {
    const v = Math.max(0, Math.floor(Number(globalDays) || 0));
    const keys = new Set(visibleItems.map(rowKey));
    setItems((prev) =>
      prev.map((i) => (keys.has(rowKey(i)) ? { ...i, extra_days: v } : i)),
    );
  };

  const setRowDays = (key: string, days: number) => {
    setItems((prev) =>
      prev.map((i) => (rowKey(i) === key ? { ...i, extra_days: days } : i)),
    );
  };

  const removeRow = (key: string) => {
    setItems((prev) => prev.filter((i) => rowKey(i) !== key));
  };

  const addUser = (item: BulkUserRow) => {
    setItems((prev) => {
      if (prev.some((p) => rowKey(p) === rowKey(item))) return prev;
      return [...prev, { ...item, extra_days: 0 }];
    });
  };

  const canSubmit =
    target !== "" && !submit.isPending &&
    (isHostTarget ? visibleItems.length > 0 : rotatable.length > 0);

  return (
    <>
      {source === "server" && (
        <Card title="Откуда брать список">
          <div className="text-xs text-zinc-500 mb-2">
            Стартовая выборка — все юзеры с активным доступом на этом
            сервере (старая модель) ИЛИ хосте (новая модель). Можно потом дописать
            дополнительных через «Добавить юзера» ниже.
          </div>
          <div className="w-full sm:w-96">
            <DarkSelect
              value={sourceSel}
              onChange={setSourceSel}
              placeholder="— выбери сервер или хост —"
              options={sourceOptions}
            />
          </div>
          {sourceSel && sourceUsers.isLoading && (
            <div className="text-xs text-zinc-500 mt-2">Загружаю юзеров…</div>
          )}
          {sourceSel && sourceUsers.data?.items.length === 0 && (
            <div className="text-xs text-amber-400 mt-2">
              Здесь нет активных юзеров.
            </div>
          )}
        </Card>
      )}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Куда переносим">
          <DarkSelect
            value={target}
            onChange={setTarget}
            placeholder="— сервер или хост —"
            options={destOptions}
          />
          <p className="text-xs text-zinc-500 mt-2">
            {isHostTarget ? (
              <>
                <b className="text-zinc-300">Хост новой модели.</b> Каждому юзеру из списка
                создаётся Subscription на этот хост (новый секрет, остаток дней +N).
                Старый доступ не трогаем, план — paid. Уже имеющие подписку тут — пропускаются.
              </>
            ) : (
              <>
                <b className="text-zinc-300">Старый сервер.</b> Строки со старым Access
                переносятся сюда (тот же expires_at, +N дней). Secret регенерируется,
                старая ссылка перестанет работать. Юзеры без Access (только новая модель) — не переносятся на сервер.
              </>
            )}
          </p>
        </Card>

        <Card title="Всем сразу +N дней (set, не add)">
          <div className="flex items-center gap-2">
            <Input
              value={globalDays}
              onChange={(e) => setGlobalDays(e.target.value)}
              placeholder="0"
              className="w-32"
            />
            <Button variant="primary" onClick={applyGlobal}>
              Применить ко всем
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            Перетирает индивидуальные значения у всех в списке. Если задал
            <code className="mx-1">+5</code> — у всех станет 5. Потом можно
            каждому отдельно поправить.
          </p>
        </Card>

        <Card title="Добавить юзера">
          <UserPicker onPick={addUser} />
        </Card>
      </div>

      <section className="mt-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-sm uppercase tracking-wider text-zinc-400">
              Список ({visibleItems.length}
              {typeFilter !== "all" ? ` из ${items.length}` : ""})
            </h2>
            <div className="flex gap-1">
              {(
                [
                  ["all", `все (${items.length})`],
                  ["test", `test (${testCount})`],
                  ["paid", `paid (${paidCount})`],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTypeFilter(key)}
                  className={`h-7 px-3 rounded-lg text-xs font-medium transition-all ${
                    typeFilter === key
                      ? "bg-brand-soft text-brand glow-ring"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04] border border-white/[0.06]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={async () => {
              const n = isHostTarget ? visibleItems.length : rotatable.length;
              const body = isHostTarget
                ? `Создать подписки для ${n} юзеров на «${targetLabel}»? Юзерам уйдёт новая ссылка, старый доступ останется.`
                : `Перенести ${n} юзеров на «${targetLabel}»? Secret будет регенерирован, старые ссылки перестанут работать.${
                    visibleItems.length !== rotatable.length
                      ? ` (${visibleItems.length - rotatable.length} без Access — пропущены)`
                      : ""
                  }`;
              if (await confirmPopup({ title: "Групповая выдача", body, confirmLabel: "Выполнить", danger: true })) {
                submit.mutate();
              }
            }}
          >
            {submit.isPending
              ? "Выполняю…"
              : isHostTarget
                ? `На хост (${visibleItems.length})`
                : `Перенести (${rotatable.length})`}
          </Button>
        </div>
        <Card>
          {visibleItems.length === 0 ? (
            <div className="text-zinc-500 text-sm py-4">
              {items.length === 0
                ? "Список пустой. Выбери сервер-источник или добавь юзеров вручную."
                : "Нет строк с выбранным фильтром."}
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>юзер</Th>
                  <Th>сейчас на</Th>
                  <Th>истекает</Th>
                  <Th>осталось</Th>
                  <Th>+ дней</Th>
                  <Th>будет до</Th>
                  <Th align="right" fit>
                    действия
                  </Th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((it) => {
                  const remain = remainingDays(it.expires_at);
                  const newExpires =
                    new Date(it.expires_at).getTime() +
                    Math.max(0, it.extra_days) * 24 * 60 * 60 * 1000;
                  const k = rowKey(it);
                  return (
                    <tr key={k}>
                      <Td>
                        <div className="text-zinc-100">
                          {it.username ? `@${it.username}` : `#${it.user_id}`}
                          {it.access_type === "test" && (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400 align-middle">
                              test
                            </span>
                          )}
                          {it.access_id == null && (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-500/15 text-sky-400 align-middle">
                              новая модель
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 font-mono">{it.telegram_id}</div>
                      </Td>
                      <Td className="text-zinc-300">{it.server_name ?? "—"}</Td>
                      <Td className="text-zinc-400">{dt(it.expires_at)}</Td>
                      <Td>{remain} дн</Td>
                      <Td>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={it.extra_days}
                          onChange={(e) =>
                            setRowDays(k, Math.max(0, parseInt(e.target.value) || 0))
                          }
                          className="h-8 w-20 px-2 rounded-md bg-black/25 border border-white/[0.08] text-sm text-zinc-100 text-right focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/20 transition"
                        />
                      </Td>
                      <Td className="text-zinc-300">{dt(new Date(newExpires).toISOString())}</Td>
                      <Td align="right" fit>
                        <RowActions
                          onSetDays={(d) => setRowDays(k, d)}
                          onRemove={() => removeRow(k)}
                          currentDays={it.extra_days}
                        />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </section>

      {hostResult && (
        <section className="mt-4">
          <Card title="Результат">
            <div className="text-sm text-emerald-300">
              В очереди {hostResult.queued} → «{hostResult.target}». Выдача идёт в фоне,
              юзерам уйдёт новая ссылка.
            </div>
          </Card>
        </section>
      )}

      {submitResult && (
        <section className="mt-4">
          <Card title="Результат">
            <div className="text-sm text-emerald-300 mb-1">✓ Успешно: {submitResult.ok}</div>
            {submitResult.errors.length > 0 && (
              <>
                <div className="text-sm text-red-300 mt-2">
                  ✗ Ошибки: {submitResult.errors.length}
                </div>
                <ul className="text-xs text-red-300/80 mt-1 space-y-1">
                  {submitResult.errors.map((e) => (
                    <li key={e.access_id}>
                      access #{e.access_id}: {e.error}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </section>
      )}

      {submit.isError && (
        <div className="mt-4 text-sm text-red-300">
          HTTP error: {(submit.error as Error).message}
        </div>
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────
// Таб «Новая выдача»
// ──────────────────────────────────────────────────────────

function NewGrantTab() {
  const [segment, setSegment] = useState("no_test");
  const [accessType, setAccessType] = useState<"test" | "paid">("test");
  const [durationDays, setDurationDays] = useState("2");
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [result, setResult] = useState<BulkGrantResponse | null>(null);

  const preview = useQuery<BulkGrantPreviewResponse>({
    queryKey: ["bulk-grant-preview", segment, accessType],
    queryFn: () =>
      api<BulkGrantPreviewResponse>(
        `/api/admin/grants/bulk-grant/preview?segment=${segment}&access_type=${accessType}`,
      ),
    enabled: previewLoaded,
  });

  const grant = useMutation<BulkGrantResponse>({
    mutationFn: () =>
      api<BulkGrantResponse>("/api/admin/grants/bulk-grant", {
        method: "POST",
        body: JSON.stringify({
          segment,
          access_type: accessType,
          duration_days: Math.max(1, parseInt(durationDays) || 1),
        }),
      }),
    onSuccess: (r) => {
      setResult(r);
      preview.refetch();
    },
  });

  const segmentInfo = GRANT_SEGMENTS.find((s) => s.value === segment);
  const total = preview.data?.total ?? 0;
  const daysNum = Math.max(1, parseInt(durationDays) || 1);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Сегмент">
          <DarkSelect
            value={segment}
            onChange={(v) => {
              setSegment(v);
              setPreviewLoaded(false);
              setResult(null);
            }}
            placeholder="— сегмент —"
            options={GRANT_SEGMENTS.map((s) => ({ value: s.value, label: s.label }))}
          />
          {segmentInfo && (
            <p className="text-xs text-zinc-500 mt-2">{segmentInfo.desc}</p>
          )}
        </Card>

        <Card title="Тип доступа">
          <div className="flex gap-2">
            <button
              onClick={() => setAccessType("test")}
              className={`flex-1 h-9 rounded-md border text-sm transition-colors ${
                accessType === "test"
                  ? "bg-brand-soft text-brand glow-ring border-transparent"
                  : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04]"
              }`}
            >
              Тестовый
            </button>
            <button
              onClick={() => setAccessType("paid")}
              className={`flex-1 h-9 rounded-md border text-sm transition-colors ${
                accessType === "paid"
                  ? "bg-brand-soft text-brand glow-ring border-transparent"
                  : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04]"
              }`}
            >
              Платный
            </button>
          </div>
        </Card>

        <Card title="Длительность (дней)">
          <Input
            value={durationDays}
            onChange={(e) => setDurationDays(e.target.value)}
            placeholder="2"
            className="w-32"
          />
          <p className="text-xs text-zinc-500 mt-2">
            {accessType === "test"
              ? `Тестовый на ${daysNum} дн. (${daysNum * 24} ч)`
              : `Платный на ${daysNum} дн.`}
          </p>
        </Card>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Button
          variant="primary"
          onClick={() => {
            setPreviewLoaded(true);
            setResult(null);
            preview.refetch();
          }}
          disabled={preview.isFetching}
        >
          {preview.isFetching ? "Загружаю…" : "Предпросмотр"}
        </Button>

        {previewLoaded && !preview.isFetching && (
          <Button
            variant="danger"
            disabled={total === 0 || grant.isPending}
            onClick={async () => {
              if (
                await confirmPopup({
                  title: "Выдать доступ всем?",
                  body: `Выдать ${accessType === "test" ? "тест" : "paid"} на ${daysNum} дн. для ${total} юзеров (сегмент: ${segmentInfo?.label})?`,
                  confirmLabel: "Выдать",
                })
              ) {
                grant.mutate();
              }
            }}
          >
            {grant.isPending ? "Выдаём…" : `Выдать всем (${total})`}
          </Button>
        )}
      </div>

      {previewLoaded && preview.data && (
        <section className="mt-4">
          <Card title={`Кандидаты (${total})`}>
            {total === 0 ? (
              <div className="text-zinc-500 text-sm py-4">
                В этом сегменте никого нет.
              </div>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>юзер</Th>
                    <Th>telegram id</Th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.candidates.slice(0, 200).map((c) => (
                    <tr key={c.user_id}>
                      <Td>
                        <div className="text-zinc-100">
                          {c.username ? `@${c.username}` : c.first_name ?? `#${c.user_id}`}
                        </div>
                      </Td>
                      <Td className="text-zinc-500 font-mono text-xs">{c.telegram_id}</Td>
                    </tr>
                  ))}
                  {total > 200 && (
                    <tr>
                      <td colSpan={2} className="px-3 py-2 text-xs text-zinc-500">
                        …и ещё {total - 200} юзеров (показаны первые 200)
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </Card>
        </section>
      )}

      {result && (
        <section className="mt-4">
          <Card title="Результат выдачи">
            <div className="text-sm text-emerald-300 mb-1">✓ Успешно: {result.ok}</div>
            {result.errors.length > 0 && (
              <>
                <div className="text-sm text-red-300 mt-2">
                  ✗ Ошибки: {result.errors.length}
                </div>
                <ul className="text-xs text-red-300/80 mt-1 space-y-1">
                  {result.errors.map((e) => (
                    <li key={e.user_id}>
                      user #{e.user_id}: {e.error}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </section>
      )}

      {grant.isError && (
        <div className="mt-4 text-sm text-red-300">
          HTTP error: {(grant.error as Error).message}
        </div>
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────
// Shared components
// ──────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-4 rounded-lg text-sm border transition-colors ${
        active
          ? "bg-brand-soft text-brand glow-ring border-transparent"
          : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04]"
      }`}
    >
      {children}
    </button>
  );
}

function UserPicker({ onPick }: { onPick: (item: BulkUserRow) => void }) {
  const [q, setQ] = useState("");
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const search = useQuery<UserSearchResponse>({
    queryKey: ["bulk-user-search", q.trim()],
    queryFn: () =>
      api<UserSearchResponse>(
        `/api/admin/users/search?q=${encodeURIComponent(q.trim())}`,
      ),
    enabled: q.trim().length >= 2,
  });

  const handlePick = async (user: UserSearchItem) => {
    setPicking(true);
    setPickerError(null);
    try {
      const rows = await api<BulkUserRow[]>(
        `/api/admin/users/${user.id}/active-paid`,
      );
      if (!rows || rows.length === 0) {
        setPickerError(
          `у ${user.username ? "@" + user.username : "#" + user.id} нет активного доступа (paid/test)`,
        );
        return;
      }
      rows.forEach(onPick);
      setQ("");
    } catch (e) {
      setPickerError((e as Error).message);
    } finally {
      setPicking(false);
    }
  };

  const found = search.data?.users ?? [];

  return (
    <div>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="@username или telegram_id"
      />
      {q.trim().length >= 2 && (
        <div className="mt-2 max-h-64 overflow-y-auto border border-zinc-800 rounded-md divide-y divide-zinc-900">
          {search.isLoading && (
            <div className="px-3 py-2 text-xs text-zinc-500">Ищу…</div>
          )}
          {!search.isLoading && found.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-500">Не нашёл</div>
          )}
          {found.map((u) => (
            <button
              key={u.id}
              disabled={picking}
              onClick={() => handlePick(u)}
              className="w-full text-left px-3 py-2 hover:bg-zinc-800/40 disabled:opacity-50"
            >
              <div className="text-sm text-zinc-100">
                {u.username ? `@${u.username}` : `#${u.id}`}
              </div>
              <div className="text-xs text-zinc-500 font-mono">
                {u.telegram_id}
                {u.first_name && ` · ${u.first_name}`}
              </div>
            </button>
          ))}
        </div>
      )}
      {pickerError && (
        <div className="mt-2">
          <Pill tone="warning">{pickerError}</Pill>
        </div>
      )}
    </div>
  );
}

function RowActions({
  currentDays,
  onSetDays,
  onRemove,
}: {
  currentDays: number;
  onSetDays: (d: number) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(currentDays));
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
  }, [open, editing]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
      setEditing(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setEditing(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    const close = () => {
      setOpen(false);
      setEditing(false);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => {
          setOpen((v) => !v);
          setDraft(String(currentDays));
          setEditing(false);
        }}
        className="h-8 w-8 rounded-md border border-zinc-800 text-zinc-300 hover:bg-zinc-800/40"
        title="Действия"
      >
        ⋯
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 50 }}
            className="min-w-[200px] rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl py-1"
          >
            {!editing ? (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800/60"
                >
                  Задать +N дней…
                </button>
                <button
                  onClick={() => {
                    onSetDays(0);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800/60"
                >
                  Сбросить (+0)
                </button>
                <button
                  onClick={() => {
                    onRemove();
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-red-300 hover:bg-red-950/40"
                >
                  Убрать из списка
                </button>
              </>
            ) : (
              <div className="p-3 space-y-2">
                <div className="text-xs text-zinc-500">+ дней</div>
                <input
                  autoFocus
                  type="text"
                  inputMode="numeric"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onSetDays(Math.max(0, parseInt(draft) || 0));
                      setOpen(false);
                    }
                  }}
                  className="h-8 w-full px-2 rounded-md bg-black/25 border border-white/[0.08] text-sm text-zinc-100 focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/20 transition"
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      setEditing(false);
                      setDraft(String(currentDays));
                    }}
                    className="h-7 px-3 text-xs rounded-md bg-zinc-900 text-zinc-300 border border-zinc-800 hover:bg-zinc-700"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={() => {
                      onSetDays(Math.max(0, parseInt(draft) || 0));
                      setOpen(false);
                    }}
                    className="h-7 px-3 text-xs rounded-md bg-brand/15 text-brand border border-brand/40 hover:bg-brand hover:text-brand-fg"
                  >
                    Сохранить
                  </button>
                </div>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
